import React, { useState, useCallback } from 'react';
import {
  FormControl,
  OutlinedInput,
  IconButton,
  Grid,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { Send as SendIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';
import AttachFile from '@mui/icons-material/AttachFile';
import { recordEvent } from '../utils/analytics-wrapper';
import { useDropzone } from 'react-dropzone';
import { loadXLSForm } from '../services/xlsx';

const style = {
  chatField: {
    backgroundColor: 'white',
    position: 'absolute',
    width: '100%',
  },
};

const ChatInput = ({
  messageRef,
  onMessageSubmitted = (_) => {},
  disabled = false,
  numberOfInputRows,
  setNumberOfInputRows,
  desktopMode,
  setCancel,
  logger,
  onResetChat,
  chats,
  supportXLSForm,
  useXLSForm,
}) => {
  const [chatMessage, setChatMessage] = useState('');

  const handleInputChange = event => {
    // count number of \n in input
    const newNumberOfInputRows = (event.target.value.match(/\n/g) || []).length + 1;
    if (newNumberOfInputRows !== numberOfInputRows) {
      setNumberOfInputRows(Math.min(newNumberOfInputRows, 4));
    }
    setChatMessage(event.target.value);
  };

  const handleEnterChat = event => {
    // detect enter but prevent shift+enter
    if (event.keyCode === 13 && !event.shiftKey) {
      handleChat(event);
    }
  };

  const handleChat = event => {
    event.preventDefault();
    onMessageSubmitted(chatMessage);
    setChatMessage('');
    recordEvent(
      {
        name: 'questionAsked',
        metrics: { questionLength: chatMessage.length }, // Character count
      },
      logger,
    );
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];

    loadXLSForm(file, (error, data) => {
      if (error) {
        console.error(error);
        useXLSForm(file.name, null, error);
      }
      else {
        useXLSForm(file.name, data, error);
      }
    });
  }, []);

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  return (
    <Grid
      container
      spacing={2}
      alignItems="center"
      justifyContent={'center'}
      style={{ backgroundColor: 'white', zIndex: 10, padding: 0 }}
    >
      <Grid item xs={1} style={{ paddingTop: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Tooltip title="Reset conversation (starts over)">
          <span>
            <IconButton onClick={onResetChat} aria-label="Reset conversation"
                        disabled={disabled || !(chats && chats.length > 1)}
                        style={{ height: 40, width: 40 }}>
              <RefreshIcon fontSize="large" />
            </IconButton>
          </span>
        </Tooltip>
      </Grid>
      <Grid item xs={1} style={{ paddingTop: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {supportXLSForm && (
          <Tooltip title="Attach XLSForm for context">
            <span>
              <IconButton {...getRootProps()} aria-label="Attach XLSForm for context"
                          disabled={disabled} style={{ height: 40, width: 40 }}>
                <input {...getInputProps()} />
                <AttachFile fontSize="large" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Grid>
      <Grid
        item
        xs={disabled ? 7 : 8}
        style={{
          paddingTop: 0,
          position: 'relative',
          height: 'calc(57px + ' + (numberOfInputRows - 1) * 23 + 'px)',
        }}
      >
        <FormControl fullWidth variant="outlined">
          <OutlinedInput
            inputRef={messageRef}
            onKeyDown={handleEnterChat}
            maxRows={4}
            multiline
            disabled={disabled}
            style={{
              ...style.chatField,
              bottom: 'calc(-57px - ' + (numberOfInputRows - 1) * 23 + 'px)',
            }}
            id="outlined-adornment-weight"
            placeholder={
              disabled
                ? desktopMode
                  ? 'Processing and responding. Please wait...'
                  : 'Please wait...'
                : 'Question or feedback...'
            }
            onChange={handleInputChange}
            value={chatMessage}
            aria-describedby="outlined-weight-helper-text"
            inputProps={{
              'aria-label': 'Chat message input',
            }}
          />
        </FormControl>
      </Grid>
      <Grid item xs={disabled ? 3 : 1} sm={disabled ? 2 : 1} style={{ paddingTop: 0 }}>
        {disabled ? (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CircularProgress style={{ height: 30, width: 30 }} />
            <IconButton
              onClick={() => setCancel(true)}
              aria-label="Cancel response"
              style={{ marginLeft: 10 }}
            >
              <CancelIcon style={{ height: 30, width: 30 }} color={'second'} />
            </IconButton>
          </div>
        ) : (
          <span>
            <Tooltip title="Send question or feedback">
              <IconButton
                disabled={disabled}
                onClick={handleChat}
                aria-label="Send question or feedback"
              >
                <SendIcon color={disabled ? 'third' : 'second'} />
              </IconButton>
            </Tooltip>
          </span>
        )}
      </Grid>
    </Grid>
  );
};

export default ChatInput;
